import { Slot } from '@radix-ui/react-slot';
import { AlertCircle, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import * as React from 'react';
import { TierPracticeFilled, TierProFilled } from '../icons';

import { cn } from '@heidi/utils';
import { Button } from '../button';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import { typographyVariant } from '../typography';

import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { bannerVariantsExperimental } from './banner.experimental.styles';
import { bannerVariants } from './banner.styles';
import type {
  BannerActionProps,
  BannerCloseProps,
  BannerContentProps,
  BannerDescriptionProps,
  BannerProps,
  BannerSubVariant,
  BannerTitleProps,
} from './banner.types';

// Default icons for specific variants
const DEFAULT_ICONS: Partial<Record<BannerSubVariant, React.ReactNode>> = {
  success: <CheckCircle2 />,
  warning: <AlertCircle />,
  error: <AlertTriangle />,
  pro: <TierProFilled size={18} />,
  practice: <TierPracticeFilled size={18} />,
};

const BannerContext = React.createContext<{ subVariant?: BannerSubVariant }>({});

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ className, subVariant = 'default', asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    const variants = isExperimentalForms ? bannerVariantsExperimental : bannerVariants;
    return (
      <BannerContext.Provider value={{ subVariant }}>
        <Comp
          {...props}
          data-experimental-form={isExperimentalForms}
          className={cn(
            variants({
              subVariant,
              className,
            })
          )}
          ref={ref}
        />
      </BannerContext.Provider>
    );
  }
);
Banner.displayName = 'Banner';

const BannerContent = React.forwardRef<HTMLDivElement, BannerContentProps>(
  ({ className, asChild = false, icon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    const { subVariant } = React.useContext(BannerContext);
    const defaultIcon = subVariant ? DEFAULT_ICONS[subVariant] : null;
    const displayIcon = icon ?? defaultIcon;

    return (
      <Comp
        ref={ref}
        className={cn('flex flex-1 items-start gap-2', className)}
        {...props}
      >
        {displayIcon && (
          <div className="flex shrink-0 items-center pt-px [&>svg]:size-4.5 [&>svg]:shrink-0">
            {displayIcon}
          </div>
        )}
        <div className="flex flex-1 flex-col gap-0.5">{children}</div>
      </Comp>
    );
  }
);
BannerContent.displayName = 'BannerContent';

const BannerTitle = React.forwardRef<HTMLParagraphElement, BannerTitleProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'p';
    return (
      <Comp
        ref={ref}
        className={cn(typographyVariant({ variant: 'p2Bold' }), className)}
        {...props}
      />
    );
  }
);
BannerTitle.displayName = 'BannerTitle';

const BannerDescription = React.forwardRef<HTMLParagraphElement, BannerDescriptionProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'p';
    // DS-1202: the experimental recipe consumes the notifications group alias
    // (= text-primary; addressable per-part in the taxonomy).
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    return (
      <Comp
        ref={ref}
        className={cn(
          typographyVariant({ variant: 'caption' }),
          'text-pretty',
          isExperimentalForms
            ? 'text-notifications-text-description'
            : 'text-text-secondary',
          className
        )}
        {...props}
      />
    );
  }
);
BannerDescription.displayName = 'BannerDescription';

/**
 * Action slot for up to two Buttons (omit the slot entirely if the banner has
 * no actions). Renders as `flex items-center gap-2` so two Buttons can sit as
 * direct children without a wrapper div.
 *
 * Two is the maximum. If you reach for three, the banner is doing too much —
 * split it, demote actions to links inside the description, or move the work
 * into a dialog.
 *
 * Action buttons only — do NOT nest `BannerClose` inside. `BannerClose` must
 * be a sibling of `BannerAction`, not a child. Nesting tightens the close
 * against the last action button with no spacing.
 */
const BannerAction = React.forwardRef<HTMLDivElement, BannerActionProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp
        ref={ref}
        className={cn('flex shrink-0 items-center gap-2', className)}
        {...props}
      />
    );
  }
);
BannerAction.displayName = 'BannerAction';

/**
 * Close button (X icon).
 *
 * Must be a direct child of `Banner`, a sibling of `BannerAction` — NEVER
 * nested inside `BannerAction`. Nesting puts it inside the action group's
 * `gap-2` flex row, leaving the close flush against the last action button.
 *
 * ```tsx
 * <Banner>
 *   <BannerContent>…</BannerContent>
 *   <BannerAction>
 *     <Button>Keep</Button>
 *   </BannerAction>
 *   <BannerClose />
 * </Banner>
 * ```
 */
const BannerClose = React.forwardRef<HTMLButtonElement, BannerCloseProps>(
  ({ className, 'aria-label': ariaLabel, ...props }, ref) => {
    const intl = useDesignSystemIntl();
    const resolvedAriaLabel =
      ariaLabel ??
      intl.formatMessage({
        defaultMessage: 'Close',
        id: '4/wcsmKBru',
        description: 'Default accessible label for the Banner close button',
      });
    return (
      <Button
        ref={ref}
        variant="ghost"
        size="xs"
        iconOnly
        flush="all"
        disableAutomaticSvgSizing
        aria-label={resolvedAriaLabel}
        className={cn(
          'opacity-50 hover:bg-transparent hover:opacity-100 hover:shadow-none active:bg-transparent active:opacity-100',
          'text-text-secondary hover:text-text-secondary active:text-text-secondary',
          className
        )}
        {...props}
      >
        <X className="size-3.5 shrink-0" />
      </Button>
    );
  }
);
BannerClose.displayName = 'BannerClose';

export {
  Banner,
  BannerAction,
  BannerClose,
  BannerContent,
  BannerDescription,
  BannerTitle,
};
